import { FC, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

import FilterOption from './FilterOption';

const Filter: FC<any> = ({
	blog,
	name,
	options,
	partners,
	selectAll,
	filterIndex,
	activeMobile,
	selectAllStatus,
	changeActiveOptions,
	changeActiveTabMobile,
}) => {
	const [dynamicOptions, changeDynamicOptions] = useState<any>([]);
	const [word, changeWord] = useState('');
	const { lang } = useContext(GlobalContext);
	const selectAllValue = 'All';

	useEffect(() => {
		changeDynamicOptions(options);
	}, [options]);

	const search = (newWord) => {
		changeWord(newWord);

		const updatedOptinos = options.filter((option) =>
			option?.optionName?.toLowerCase()?.match(newWord?.toLowerCase()),
		);

		changeDynamicOptions(updatedOptinos);
	};

	return (
		<Wrapper className="filter" $blog={blog}>
			<Select
				className="filter__select"
				$activeMobile={activeMobile}
				onClick={changeActiveTabMobile}
			>
				<Type $blog={blog}>
					{name}

					<Quantity $selectAllStatus={selectAllStatus}>
						{selectAllStatus ? (
							translate(lang, { en: ' ', ru: '', ua: ' ' })
						) : (
							<QuantityCircle>
								{options.filter((option) => option.active).length}
							</QuantityCircle>
						)}
					</Quantity>
				</Type>
			</Select>

			<Dropdown className="filter__dropdown" $activeMobile={activeMobile}>
				<DropdownContent>
					<Search>
						<Input
							type="text"
							value={word}
							onChange={(e) => search(e.target.value)}
						/>
						<SearchIcon src="/images/icons/search.svg" />
					</Search>

					<Options>
						{selectAllValue.toLowerCase().match(word.toLowerCase()) && (
							<FilterOption
								active={selectAllStatus}
								text={
									partners
										? translate(lang, {
												en: 'All partners',
												ru: 'Все партнеры',
												ua: 'Всі партнери',
											})
										: translate(lang, { en: 'All', ru: 'Все', ua: 'Всі' })
								}
								onClick={() => selectAll(filterIndex)}
							/>
						)}

						{dynamicOptions?.map((option, index) => (
							<FilterOption
								key={`dynamic_option_${index}`}
								text={option.optionName}
								active={option.active}
								onClick={() =>
									changeActiveOptions(
										!option.active,
										filterIndex,
										option.optionName,
									)
								}
							/>
						))}

						{!selectAllValue.toLowerCase().match(word.toLowerCase()) &&
							dynamicOptions.length === 0 && (
								<NoResults>
									{translate(lang, {
										en: 'Sorry, no results.',
										ua: 'Нічого не знайдено.',
										ru: 'Ничего не найдено.',
									})}
								</NoResults>
							)}
					</Options>

					<ResetButton
						$active={!selectAllStatus}
						onClick={() => selectAll(filterIndex)}
					>
						{translate(lang, { en: 'Reset', ru: 'Сбросить', ua: 'Скинути' })}
					</ResetButton>
				</DropdownContent>
			</Dropdown>
		</Wrapper>
	);
};

const Dropdown = styled.div<{ $activeMobile?: boolean }>`
	visibility: hidden;
	position: absolute;
	width: 260px;
	opacity: 0;
	left: 50%;
	transform: translateX(-50%);
	top: 45px;
	transition: 0.3s;

	&:after {
		content: '';
		position: absolute;
		height: 90%;
		width: 90%;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		background-color: var(--color-blue);
		opacity: 0.25;
		filter: blur(32px);
		border-radius: 20px;
		z-index: -1;
	}

	&:before {
		content: '';
		position: absolute;
		height: 64%;
		width: 110%;
		left: 50%;
		top: 75%;
		transform: translate(-50%, -50%);
		background-color: var(--color-blue);
		opacity: 0.15;
		filter: blur(36px);
		border-radius: 20px;
		z-index: -1;
	}

	@media (max-width: 600px) {
		width: 345px;
		max-width: 95%;

		&:after {
			display: none;
		}

		&:before {
			display: none;
		}

		box-shadow: 0px 10px 30px rgba(34, 23, 106, 0.05);

		${({ $activeMobile }) =>
			$activeMobile &&
			css`
				opacity: 1;
				visibility: visible;
			`}
	}
`;

const DropdownContent = styled.div`
	margin-top: 20px;
	padding-bottom: 0;
	padding-top: 8px;
	background-color: var(--color-white-30);
	border-radius: 18px;
`;

const Options = styled.div`
	max-height: 330px;
	overflow: scroll;

	@media (max-width: 600px) {
		max-height: 43vh;
		height: 43vh;
	}

	@media (max-height: 660px) {
		max-height: 39vh;
		height: 39vh;
	}

	@media (max-height: 600px) {
		max-height: 35vh;
		height: 35vh;
	}

	@media (max-height: 560px) {
		max-height: 31vh;
		height: 31vh;
	}
`;

const ResetButton = styled.div<{ $active: boolean }>`
	width: 100%;
	cursor: pointer;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 15px;
	text-align: center;
	text-transform: uppercase;
	padding: 13px 0;
	background-color: var(--color-white-40);
	border-bottom-left-radius: 18px;
	border-bottom-right-radius: 18px;
	margin-top: 5px;
	color: var(--color-purple-50);
	opacity: 0.5;

	@media (max-width: 600px) {
		display: none;
	}

	${({ $active }) =>
		$active &&
		css`
			color: var(--color-purple);
			opacity: 1;
		`}
`;

const Quantity = styled.div<{ $selectAllStatus?: boolean }>`
	position: absolute;

	${({ $selectAllStatus }) =>
		$selectAllStatus
			? css`
					top: -2px;
					right: -28px;
				`
			: css`
					top: -2px;
					right: -22px;
				`}

	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
	transition:
		color 0.3s,
		background-color 0.3s;

	@media (max-width: 360px) {
		font-size: 12px;
		line-height: 16px;
		right: -17px;
	}
`;

const QuantityCircle = styled.div`
	width: 20px;
	height: 20px;
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 13px;
	line-height: 20px;
	border-radius: 50%;
	transition:
		color 0.3s,
		background-color 0.3s;
	color: var(--color-white);
	background-color: var(--color-purple);

	@media (max-width: 500px) {
		font-size: 12px;
		width: 16px;
		height: 16px;
		line-height: 17px;
	}
`;

const Select = styled.div<{ $activeMobile?: boolean }>`
	padding: 9px 46px 11px 31px;
	background-color: var(--color-white-30);
	font-family: var(--font-sans);
	font-size: 18px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	cursor: pointer;
	position: relative;
	z-index: 20;
	transition: 0.3s;

	@media (max-width: 600px) {
		font-size: 16px;
		line-height: 23px;
		padding-left: 25px;
		padding-right: 40px;

		${({ $activeMobile }) =>
			$activeMobile &&
			css`
				background-color: var(--color-purple);
				color: var(--color-white);

				${Quantity} {
					color: var(--color-white);
				}

				${QuantityCircle} {
					color: var(--color-purple);
					background-color: var(--color-white);
				}
			`}
	}

	@media (max-width: 403px) {
		font-size: 14px;
		line-height: 20px;
		padding-left: 21px;
		padding-right: 30px;
	}

	@media (max-width: 360px) {
		font-size: 13px;
		line-height: 16px;
		padding-left: 19px;
		padding-right: 28px;
	}
`;

const Wrapper = styled.div<{ $blog?: boolean }>`
	position: relative;
	z-index: 30;

	@media (min-width: 600px) {
		&:hover {
			z-index: 40;

			${Dropdown} {
				visibility: visible;
				opacity: 1;
			}

			${Select} {
				background-color: var(--color-purple);
				color: var(--color-white);
			}

			${Quantity} {
				color: var(--color-white);
			}

			${QuantityCircle} {
				color: var(--color-purple);
				background-color: var(--color-white);
			}
		}
	}

	@media (max-width: 600px) {
		position: static;
	}

	${({ $blog }) =>
		$blog &&
		css`
			@media (max-width: 600px) {
				width: 90% !important;
			}
			${Select} {
				@media (max-width: 600px) {
					display: flex;
					justify-content: center;
				}
			}
		`}
`;

const Type = styled.div<{ $blog?: boolean }>`
	position: relative;

	${({ $blog }) =>
		$blog &&
		css`
			@media (max-width: 600px) {
				width: max-content;
			}
		`}
`;

const Search = styled.div`
	padding-top: 15px;
	padding-left: 15px;
	padding-right: 15px;
	margin-top: 0;
	margin-bottom: 15px;
	position: relative;

	@media (max-width: 500px) {
		padding-top: 10px;
		padding-bottom: 10px;
	}
`;

const Input = styled.input`
	width: 100%;
	box-sizing: border-box;
	padding-left: 20px;
	padding-right: 30px;
	height: 50px;
	background-color: var(--color-white-30);
	border: 1px solid var(--color-purple);
	border-radius: 12px;
	font-family: var(--font-sans);
	font-size: 18px;
	line-height: 25px;
	transition: 0.3s;

	@media (max-width: 500px) {
		height: 40px;
		appearance: none;
		box-shadow: none;
	}

	&:hover {
		background-color: var(--color-white-40);
	}

	&:focus {
		outline: none;
		background-color: var(--color-white-40);
		color: var(--color-black);
	}
`;

const SearchIcon = styled.img`
	width: 24px;
	display: block;
	position: absolute;
	right: 27px;
	top: 28px;

	@media (max-width: 500px) {
		top: 17px;
	}
`;

const NoResults = styled.div`
	font-family: var(--font-sans);
	color: var(--color-purple-50);
	font-size: 16px;
	line-height: 25px;
	margin: 7px 15px;
	padding-top: 8px;
	padding-bottom: 8px;
	padding-left: 25px;
`;

export default Filter;
